import { useEffect, useRef, useState } from "react";

interface CurrencyData {
  currencyPair: string;
  type: string;
  buyRate: number;
  sellRate: number;
}

interface UseWebSocketReturn {
  data: CurrencyData[];
  connectionStatus: string;
}

export const useWebSocket = (url: string): UseWebSocketReturn => {
  const [data, setData] = useState<CurrencyData[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<string>("Bağlanıyor...");
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disposedRef = useRef(false);
  const attemptRef = useRef(0);

  useEffect(() => {
    disposedRef.current = false;
    attemptRef.current = 0;

    const clearReconnectTimer = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposedRef.current) return;
      clearReconnectTimer();
      const attempt = attemptRef.current;
      const delay = Math.min(30000, 1000 * Math.pow(2, attempt)); // 1s, 2s, 4s... max 30s
      attemptRef.current = attempt + 1;
      setConnectionStatus("Yeniden Bağlanıyor...");
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    };

    const connect = () => {
      if (disposedRef.current) return;
      clearReconnectTimer();

      try {
        wsRef.current?.close();
      } catch {
        // ignore
      }

      setConnectionStatus("Bağlanıyor...");
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setConnectionStatus("Bağlandı");
      };

      ws.onmessage = (event) => {
        try {
          const rawData = JSON.parse(event.data);
          if (!Array.isArray(rawData)) return;

          const formattedData: CurrencyData[] = rawData.map((item) => ({
            currencyPair: String(item?.Code ?? "").trim(),
            type: String(item?.Category ?? "").toUpperCase().trim(),
            buyRate: Number(item?.Bid ?? 0) || 0,
            sellRate: Number(item?.Ask ?? 0) || 0,
          }));

          // Aynı Code birden fazla gelirse, son geleni tut.
          const deduped = Array.from(
            new Map(formattedData.filter((x) => x.currencyPair).map((x) => [x.currencyPair, x])).values()
          );

          setData(deduped);
        } catch (error) {
          console.error("WebSocket veri çözümleme hatası:", error);
        }
      };

      ws.onerror = () => {
        setConnectionStatus("Bağlantı Hatası");
        // Bazı ortamlarda onerror + onclose birlikte gelir; reconnect'i onclose yönetsin.
      };

      ws.onclose = () => {
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      disposedRef.current = true;
      clearReconnectTimer();
      try {
        wsRef.current?.close();
      } catch {
        // ignore
      } finally {
        wsRef.current = null;
      }
    };
  }, [url]);

  return { data, connectionStatus };
};